import types from '../../fieldOptions/sessionSaleTypes'
import sessionSaleFlags from '../../fieldOptions/sessionSaleFlags'
import DateToggle from '../date-toggle'
import FlagList from '../flag-list'
import DiscountSelector from './discount-selector'
import slugify from '../../slugify'
import moment from 'moment'
import { offsetToContentTimezone } from '../../dates'
import BooleanToggle from '../boolean-toggle'
import DiscountWindow from './discount-window'
import ConditionFields from '../dynamic-conditions/condition-fields'
const qs = require('qs')

const isFsp = resource => resource.type === 'fsp'
const isGeneric = resource => resource.type === 'generic'
const isFspOrGeneric = resource => isFsp(resource) || isGeneric(resource)
const showEarlyBird = resource => isFspOrGeneric(resource) && qs.parse(window.location.search, { ignoreQueryPrefix: true })?.showDeprecatedFields === 'true'
const startOfDay = value => offsetToContentTimezone(moment(value).startOf('day').toDate())
const endOfDay = value => offsetToContentTimezone(moment(value).endOf('day').toDate())

export default [
  {
    name: 'Type',
    prop: 'type',
    type: 'select',
    options: types
  },
  { name: 'Title', prop: 'title', type: 'text' },
  {
    name: 'Session',
    prop: 'session_id',
    type: 'autocomplete',
    references: 'admin/sessions',
    referenceProp: 'name',
    displayProp: 'long_name'
  },
  {
    name: 'URL',
    prop: 'slug',
    type: 'text',
    filter: slugify
  },
  { name: 'Meta Title', prop: 'meta_title', type: 'text' },
  { name: 'Meta Keywords', prop: 'meta_keywords', type: 'text' },
  { name: 'Meta Description', prop: 'meta_description', type: 'text' },
  { name: 'Meta Image', prop: 'meta_image', type: 'text' },
  [{
    name: 'Reservation Product',
    prop: 'reservation_product_id',
    type: 'autocomplete',
    references: 'admin/products',
    referenceProp: 'name',
    showIf: isFspOrGeneric
  }, {
    name: 'Waitlist Product',
    prop: 'waitlist_product_id',
    type: 'autocomplete',
    references: 'admin/products',
    referenceProp: 'name',
    showIf: isFspOrGeneric
  }],
  [
    { name: 'Max Attendees', prop: 'max_attendees', type: 'number', showIf: isFspOrGeneric },
    { name: 'Max Attendees per Institution', prop: 'max_attendees_per_institution', type: 'number', showIf: isFsp }
  ],
  [
    {
      name: 'Early Bird Pricing Start',
      prop: 'early_bird_price_start_at',
      type: 'date',
      showIf: showEarlyBird,
      filter: startOfDay
    },
    {
      name: 'Early Bird Pricing End',
      prop: 'early_bird_price_end_at',
      type: 'date',
      showIf: showEarlyBird,
      filter: endOfDay
    },
    {
      name: 'Early Bird Discount',
      prop: 'early_bird_discount_id',
      type: 'component',
      showIf: showEarlyBird,
      component: DiscountSelector
    }
  ],
  [
    {
      name: 'Pre Reg Start',
      prop: 'pre_registration_start_at',
      type: 'datetime',
      showIf: isFsp
    },
    {
      name: 'Pre Reg End',
      prop: 'pre_registration_end_at',
      type: 'datetime',
      showIf: isFsp
    }
  ],
  [
    {
      name: 'Open Reg Start',
      prop: 'open_registration_start_at',
      type: 'datetime'
    },
    {
      name: 'Open Reg End',
      prop: 'open_registration_end_at',
      type: 'datetime'
    }
  ],
  [
    { name: 'Advertised Start', prop: 'advertised_start_date', type: 'date' },
    { name: 'Advertised End', prop: 'advertised_end_date', type: 'date' }
  ],

  {
    name: 'Discount Windows',
    prop: 'discount_windows',
    type: 'repeater',
    newValue: ({ resource }) => ({ name: '', start_at: null, end_at: null, session_sale_id: resource.id, discounts: [] }),
    component: DiscountWindow
  },
  {
    name: 'Granted Membership Plan',
    prop: 'granted_membership_plan_id',
    type: 'autocomplete',
    references: 'admin/membership_plans',
    referenceProp: 'name'
  },
  {
    name: 'Hard Date Expiration',
    prop: 'is_hard_date_membership_expiration',
    type: 'component',
    component: BooleanToggle
  },
  [
    {
      name: 'Membership Expiration Date',
      prop: 'granted_membership_expiration_date',
      type: 'date',
      showIf: resource => resource.is_hard_date_membership_expiration
    },
    {
      name: 'Membership Expiration Days From Registration',
      prop: 'granted_membership_expiration_days',
      type: 'number',
      filter: (value) => value <= 0 ? 0 : value,
      showIf: resource => !resource.is_hard_date_membership_expiration
    }
  ],
  {
    name: 'Published',
    prop: 'published_at',
    type: 'component',
    component: DateToggle
  },
  {
    name: 'Enable Invoice Request',
    prop: 'enable_invoice_request',
    type: 'component',
    component: BooleanToggle,
    showIf: isFspOrGeneric
  },
  {
    name: 'Required user flags',
    prop: 'flags',
    type: 'component',
    component: FlagList,
    componentProps: { flags: sessionSaleFlags },
    showIf: isGeneric
  },
  {
    // Leave empty to show to everyone (subject to the rules above). Add a row to
    // restrict visibility on the events calendar to matching logged-in users, and/or
    // on an institution's whitelabel hub page - for a row to show on a hub page it
    // must set ONLY Associated Institution, since a hub page has no logged-in viewer
    // to evaluate the other fields against. Multiple rows are OR'd together.
    name: 'Display Conditions',
    prop: 'display_conditions',
    type: 'repeater',
    newValue: () => ({}),
    component: ConditionFields
  },
  {
    name: 'Reserve Page Content',
    prop: 'reserve_page_content',
    type: 'wysiwyg',
    showIf: isFspOrGeneric,
    helperText: 'Custom content displayed on the left side of the /reserve payment page. Leave blank to center the payment form.'
  },
  {
    name: 'Reserve Page Footer Content',
    prop: 'reserve_page_footer_content',
    type: 'wysiwyg',
    showIf: isFspOrGeneric,
    helperText: 'Custom footer content displayed below the payment section on the /reserve page.'
  }
]
